import { faBed, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Box, Popover, Button, IconButton, Typography } from '@vapor/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { calculateAdditionalServicePrice } from 'src/utils/additional-services-utils'
import { ReservationCartRow } from '../../form/reservation-new.type'
import ResettableTextField from '../resettable-text-field'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { useChildrenQuery } from 'src/api/api.generated'
import ReservationCartServices from './reservation-cart-services'

type ReservationCartItemProps = {
  item: ReservationCartRow
  isEditable: boolean
  propertyId: string
  onUpdateRow?: (row: ReservationCartRow | ReservationCartRow[]) => void
  onDeleteRow?: (row: ReservationCartRow) => void
}

export const ReservationCartItem = (props: ReservationCartItemProps) => {
  const { item, isEditable, propertyId } = props
  const { id: tenantId } = useTenant()
  const localizeString = useLocalizeString()
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()

  const { data: childrenResult } = useChildrenQuery({
    variables: {
      filter: {
        propertyId: {
          eq: propertyId,
        },
        tenantId: {
          eq: tenantId,
        },
      },
    },
  })

  //#region fields
  const resourceTypeName = useMemo(() => item.resourceType.code ?? (typeof item.resourceType.name === 'string' ? item.resourceType.name : localizeString(item.resourceType.name)), [item])

  const occupancy = useMemo(() => {
    const { adults, children } = item.occupancy
    const childrenNumber = children?.map((c) => c.quantity).reduce((acc, val) => acc + val, 0)

    const occupancy =
      childrenNumber > 0
        ? t('pms.booking.reservation_new.cart_occupancy_adults_children', { adults: adults, children: childrenNumber })
        : t('pms.booking.reservation_new.cart_occupancy_adults', { adults: adults })

    return occupancy
  }, [item.occupancy])

  const rate = useMemo(() => {
    return typeof item.rate.name === 'string' ? item.rate.name : localizeString(item.rate.name)
  }, [item.rate])

  const chargeCategoryName = useMemo(() => {
    return typeof item.chargeCategory.name === 'string' ? item.chargeCategory.name : localizeString(item.chargeCategory.name)
  }, [item.chargeCategory.name])

  const additionalServicesPrice = useMemo(() => {
    return (
      item.addedAdditionalServices
        ?.map((as) => {
          return calculateAdditionalServicePrice({
            adults: item.occupancy.adults,
            children: item.occupancy.children,
            price: as.price,
            days: item.nights,
            pricingType: as.pricingType,
            automaticForAdults: as.automaticForAdults,
            automaticForChildren: as.automaticForChildren,
            quantity: as.quantity,
            childPrices: as.childPrices,
            priceZero: as.priceZero,
          })
        })
        .reduce((prev, acc) => prev + acc, 0) ?? 0
    )
  }, [JSON.stringify(item)])

  //#endregion

  //#region popover

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClosePopOver = () => {
    setAnchorEl(null)
  }

  //#endregion

  return (
    <>
      <Stack m={1} sx={{ backgroundColor: 'white', width: '100%' }}>
        <Stack px={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="titleXSmall" style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '1', WebkitBoxOrient: 'vertical' }}>
              <b>{resourceTypeName}</b>
            </Typography>
            <IconButton disabled={!props.isEditable} size="small" color="error" onClick={handleDelete}>
              <FontAwesomeIcon icon={faTrash} />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1} pb={1}>
            <Typography variant="caption"> {rate}</Typography>
            <Typography variant="caption"> {occupancy}</Typography>
            <Typography variant="caption"> {chargeCategoryName}</Typography>
          </Stack>

          <Stack direction="column" spacing={0.4}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-start" alignItems="center">
              <FontAwesomeIcon icon={faBed} color="var(--QuickSilver)" style={{ width: '18px', height: '12px', paddingBottom: '1px' }} />
              <Typography variant="caption">{t('pms.booking.reservation_new.stay_label', { price: `${formatCurrency(item.averagePrice, currency)}` })}</Typography>
            </Stack>
            <ReservationCartServices item={item} childrenResult={childrenResult} isEditable={props.isEditable} onUpdateRow={props.onUpdateRow} />
          </Stack>
        </Stack>

        <Box mt={1} sx={{ width: '100%', height: '2px', backgroundColor: 'var(--AzureishWhite)' }} />

        <Stack direction="row" justifyContent="flex-end" alignItems="center" p={1}>
          <Box maxWidth="150px">
            <ResettableTextField
              disabled={!isEditable}
              changeOnBlur
              initialValue={item.initialTotalPrice + additionalServicesPrice}
              min={additionalServicesPrice}
              value={item.totalPrice + additionalServicesPrice}
              onChange={(value) => {
                item.averagePrice = Math.round(value ? (value - additionalServicesPrice) / item.nights : 0)
                item.totalPrice = value ? value - additionalServicesPrice : 0

                if (props.onUpdateRow) props.onUpdateRow(item)
              }}
              resetPosition={'LEFT'}
            />
          </Box>
        </Stack>
      </Stack>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClosePopOver}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack direction="column" spacing={1} m={1}>
          <Typography variant="body">{t('pms.booking.reservation_new.cart_confirm_deletion')}</Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button size="small" onClick={handleClosePopOver}>
              {t('commons.cancel')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => {
                if (item && props.onDeleteRow) {
                  props.onDeleteRow(item)
                }
                handleClosePopOver()
              }}
            >
              {t('commons.yes')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
